export const INSTALL_PATH = "/integrations/hibob/install";

export const PAGE_TITLE = "Connect HiBob | Traxxion";

export const GENERIC_ERROR =
	"Something went wrong while connecting. Please try again, or contact support@traxxion.ai.";

export const ADMIN_ONLY =
	"Only a Traxxion administrator can connect HiBob. Ask one of your admins to finish this step.";

export const TENANT_DOMAIN_MISSING =
	"Your Traxxion account has no web address set up for connecting HiBob. Contact support@traxxion.ai.";

export const TOO_MANY_ATTEMPTS = "Too many attempts. Wait a minute and try again.";

const INVALID_LINK = "This connection link isn't valid anymore. Please start again.";

const RESULT_MESSAGES = {
	expired: { message: "This connection link expired. Please start again.", canRestart: true },
	already_used: { message: INVALID_LINK, canRestart: true },
	invalid_state: { message: INVALID_LINK, canRestart: true },
	missing_state: { message: INVALID_LINK, canRestart: true },
	browser_mismatch: {
		message: "Please finish connecting in the same browser you started in.",
		canRestart: true,
	},
	denied: {
		message: "Access wasn't approved in HiBob, so nothing was connected.",
		canRestart: true,
	},
	already_linked: {
		message:
			"This HiBob company is already connected to another Traxxion account. Contact support@traxxion.ai.",
		canRestart: false,
	},
	already_connected: {
		message: "HiBob is already connected to this Traxxion account.",
		canRestart: false,
	},
};

// The reason comes from the query string: it only ever selects one of the fixed messages.
export const getResultMessage = (reason) => {
	if (typeof reason === "string" && Object.prototype.hasOwnProperty.call(RESULT_MESSAGES, reason)) {
		return RESULT_MESSAGES[reason];
	}
	return { message: GENERIC_ERROR, canRestart: true };
};

// Settings -> Integration -> HiBob. Values are i18n keys; codes from the API
// only ever select one of them.
const pick = (map, code, fallback) =>
	typeof code === "string" && Object.prototype.hasOwnProperty.call(map, code)
		? map[code]
		: fallback;

const DISCONNECT_REASONS = {
	uninstalled: "hibob-reason-uninstalled",
	refresh_unauthorized: "hibob-reason-refresh-unauthorized",
	// A refreshed token still refused by the API: the same thing for the admin.
	api_unauthorized: "hibob-reason-refresh-unauthorized",
	manual: "hibob-reason-manual",
};
export const getDisconnectReasonKey = (reason) =>
	pick(DISCONNECT_REASONS, reason, "hibob-reason-other");

const SYNC_STATUSES = {
	running: { key: "hibob-sync-running", color: "info" },
	completed: { key: "hibob-sync-completed", color: "success" },
	completed_with_errors: { key: "hibob-sync-completed-with-issues", color: "warning" },
	failed: { key: "hibob-sync-failed", color: "danger" },
	disconnected: { key: "hibob-sync-stopped", color: "secondary" },
};
export const getSyncStatus = (status) => pick(SYNC_STATUSES, status, null);

const SYNC_TRIGGERS = {
	connected: "hibob-trigger-connected",
	reconnected: "hibob-trigger-connected",
	nightly: "hibob-trigger-nightly",
	manual: "hibob-trigger-manual",
};
export const getSyncTriggerKey = (trigger) => pick(SYNC_TRIGGERS, trigger, null);

// Table order. Steps the page does not know are left out.
export const SYNC_STEPS = [
	{ step: "users", key: "hibob-step-users" },
	{ step: "reference", key: "hibob-step-reference" },
	{ step: "jobHistory", key: "hibob-step-job-history" },
	{ step: "expectedHours", key: "hibob-step-expected-hours" },
	{ step: "timeoff", key: "hibob-step-timeoff" },
];

const SYNC_ERRORS = {
	not_connected: "hibob-error-not-connected",
	sync_running: "hibob-error-sync-running",
	rate_limited: "hibob-error-rate-limited",
};
export const getSyncErrorKey = (reason) => pick(SYNC_ERRORS, reason, "hibob-error-generic");

const DISCONNECT_ERRORS = {
	not_connected: "hibob-error-not-connected",
	sync_running: "hibob-error-disconnect-sync-running",
};
export const getDisconnectErrorKey = (reason) =>
	pick(DISCONNECT_ERRORS, reason, "hibob-error-generic");

// Issue codes of a sync step (closed set from apisync, see HIBOB_INTEGRATION.md).
const ISSUES = {
	EMPLOYEE_NUMBER_DIFFERS: "hibob-issue-employee-number-differs",
	E_AMBIGUOUS_MATCH: "hibob-issue-ambiguous-match",
	SUPERVISOR_NOT_FOUND: "hibob-issue-supervisor-not-found",
	SUPERVISOR_NOT_SYNCED: "hibob-issue-supervisor-not-synced",
	UNMATCHED_EXISTING: "hibob-issue-unmatched-existing",
	ABSENT_FROM_HIBOB: "hibob-issue-absent-from-hibob",
	TERMINATED_NEVER_SEEN: "hibob-issue-terminated-never-seen",
	PREVIOUS_BOB_COMPANY: "hibob-issue-previous-bob-company",
	E_MISSING_REQUIRED: "hibob-issue-missing-required",
	E_INVALID_VALUE: "hibob-issue-invalid-value",
	E_LOOKUP_MISS: "hibob-issue-lookup-miss",
	E_DUPLICATE: "hibob-issue-duplicate",
	E_TRANSFORM: "hibob-issue-transform",
	E_DB_WRITE: "hibob-issue-db-write",
	E_DEFERRED: "hibob-issue-deferred",
	E_DEPENDENCY_TIMEOUT: "hibob-issue-deferred",
};
export const getIssueKey = (code) => pick(ISSUES, code, "hibob-issue-other");

// Time off to HiBob: push issue codes from the status' writeback block.
const WRITEBACK_ISSUES = {
	no_mapping: "hibob-wb-issue-no-mapping",
	not_connected: "hibob-wb-issue-not-connected",
	no_timeoff_write: "hibob-wb-issue-no-scope",
	id_not_found: "hibob-wb-issue-id-not-found",
	unsupported_shape: "hibob-wb-issue-unsupported-shape",
	zero_duration: "hibob-wb-issue-zero-duration",
	unparsable_date: "hibob-wb-issue-bad-date",
	end_before_start: "hibob-wb-issue-bad-date",
	not_approved: "hibob-wb-issue-not-approved",
	request_not_found: "hibob-wb-issue-not-approved",
	disconnected: "hibob-wb-issue-not-connected",
};
// HiBob refusals arrive as hibob_<status>_<HiBob key>; known keys get their own words.
const WRITEBACK_HIBOB_KEYS = {
	"loa.timeoff.request.policy_type_does_not_exist_for_employee": "hibob-wb-issue-not-on-policy",
	"timeoff_reject_reason.requestInHoursNotAllowed": "hibob-wb-issue-no-hours",
};
export const getWritebackIssueKey = (code) => {
	if (typeof code !== "string") {
		return "hibob-wb-issue-other";
	}
	const refused = /^hibob_\d{3}_(.+)$/.exec(code);
	if (refused) {
		return pick(WRITEBACK_HIBOB_KEYS, refused[1], "hibob-wb-issue-refused");
	}
	return pick(WRITEBACK_ISSUES, code, /^hibob_\d{3}$/.test(code) ? "hibob-wb-issue-refused" : "hibob-wb-issue-other");
};
